import { type ClassifiedPricingPackages } from "../../types"

import type { OrderLine, Product, PurchaseOrderPurchasingCost } from "@/graphql/codegen/graphql"
import {
  convertFromTon,
  convertToTon,
  getConvertedWeightPerPallet,
  getTonUomId,
  isProductInTon,
  isTonUom,
} from "@/utils/conversion"

interface TransformQuantityFieldArgs {
  currentQuantity: number
  currentUomId: string
  name: string
  product: Product
  value: string
}

const BLANK_QUANTITY = {
  numberOfPallets: 0,
  quantityInUom: 0,
}

export const transformQuantityField = ({
  currentQuantity,
  currentUomId,
  name,
  product,
  value,
}: TransformQuantityFieldArgs): Partial<OrderLine> | null => {
  if (!product?.palletUsWeightUom) {
    return BLANK_QUANTITY
  }

  const isProductSetInTons = isProductInTon(product)

  switch (name) {
    case "numberOfPallets": {
      const numberValue = parseFloat(value) || 0
      const weightPerPallet = getConvertedWeightPerPallet(currentUomId ?? "", product)
      let quantity = 0
      const isTonSelected = isTonUom(currentUomId)

      quantity = isProductSetInTons
        ? isTonSelected
          ? numberValue
          : convertFromTon(numberValue, getTonUomId(currentUomId))
        : convertToTon(numberValue, currentUomId)

      return {
        numberOfPallets: numberValue,
        quantityInUom: quantity * weightPerPallet,
      }
    }

    case "quantityInUom": {
      const numberValue = parseFloat(value) || 0
      const weightPerPallet = getConvertedWeightPerPallet(currentUomId ?? "", product)
      let numberOfPallets = 0

      if (weightPerPallet) {
        const isTonSelected = isTonUom(currentUomId)

        const quantity = isProductSetInTons
          ? isTonSelected
            ? numberValue
            : convertToTon(numberValue, getTonUomId(currentUomId))
          : convertFromTon(numberValue, currentUomId)

        numberOfPallets = quantity / weightPerPallet
      }

      return {
        quantityInUom: numberValue,
        numberOfPallets,
      }
    }

    case "uomId": {
      const weightPerPallet = getConvertedWeightPerPallet(value, product)

      let numberOfPallets = 0

      const isTonSelected = isTonUom(value)

      if (weightPerPallet) {
        const quantity = isProductSetInTons
          ? isTonSelected
            ? currentQuantity
            : convertToTon(currentQuantity, getTonUomId(value))
          : convertFromTon(currentQuantity, value)

        numberOfPallets = quantity / weightPerPallet
      }

      return {
        uomId: value,
        quantityInUom: currentQuantity,
        numberOfPallets,
      }
    }

    default:
      return BLANK_QUANTITY
  }
}

export const updateLineValue = ({ name, value }: { name: string; value: string }): Partial<OrderLine> | null => {
  const numberValue = parseFloat(value) || 0

  switch (name) {
    case "numberOfPallets":
    case "quantityInUom":
      return {
        [name]: numberValue,
      }
    case "uomId": {
      return {
        [name]: value,
      }
    }

    default: {
      return null
    }
  }
}

export const getUpdatedLines = ({
  rowIndex,
  orderLines,
  pricingPackage,
  tierPrice,
}: {
  rowIndex: number
  orderLines: OrderLine[]
  pricingPackage?: PurchaseOrderPurchasingCost
  tierPrice: number
}): OrderLine[] | null => {
  const isManualPrice = orderLines[rowIndex]?.isManualPriceSet
  if (isManualPrice) {
    return null
  }

  // Check if the tier price has changed
  const currentOrderLine = orderLines[rowIndex]

  if (currentOrderLine?.pricePerUom === tierPrice) {
    return null
  }

  return orderLines.map((line, index) =>
    index === rowIndex
      ? {
          ...line,
          pricePerUom: tierPrice,
          ...(pricingPackage?.uomId && { priceUomId: pricingPackage?.uomId }),
        }
      : line
  )
}

export const getUpdateOrderLinesDetails = ({
  productId,
  pricingList,
  orderLineDetails,
}: {
  productId: number
  pricingList: ClassifiedPricingPackages
  orderLineDetails: Record<number, ClassifiedPricingPackages>
}): Record<number, ClassifiedPricingPackages> | null => {
  if (!pricingList.currentPricing) {
    return null
  }

  const updatedOrderLineDetails = {
    ...orderLineDetails,
    [productId]: pricingList,
  }

  const currentPricing = orderLineDetails[productId]?.currentPricing
  if (JSON.stringify(currentPricing) !== JSON.stringify(pricingList.currentPricing)) {
    return updatedOrderLineDetails
  }

  return null
}
